import { deepEqual } from "../../../utils/tools.mjs";
import { customerDateOfBirthSchema } from "../../../utils/dateOfBirth.mjs";
import { zodFlavorId } from "../../ids.mjs";
import { z as z$1 } from "zod";

// src/models/vendors/bridge/syncTargets.ts
/**
* Customer fields that map to Bridge Patient v2 demographics we upsert.
* Must stay aligned with patient mapping in
* `functions/node/bridge/src/managers/bridgePatientManager.ts`.
*
* Excluded intentionally:
* - `gender` — Bridge Patient v2 has no gender field (unlike Canvas FHIR).
* - address — Bridge supports address but Customer has no address fields to sync.
* - coverage / metadata — vendor-owned; preserved from Bridge GET, not customer-driven.
*/
const BRIDGE_PATIENT_CUSTOMER_SYNC_FIELDS = [
	"firstName",
	"lastName",
	"dateOfBirth",
	"email",
	"mobilePhone"
];
function pickBridgePatientCustomerSyncFields(data) {
	return Object.fromEntries(BRIDGE_PATIENT_CUSTOMER_SYNC_FIELDS.map((key) => [key, data[key]]));
}
/** True when sync-relevant customer fields differ; new customers (no before) count as changed. */
function bridgePatientCustomerSyncFieldsChanged(before, after) {
	if (!after) return false;
	if (!before) return true;
	return !deepEqual(pickBridgePatientCustomerSyncFields(before), pickBridgePatientCustomerSyncFields(after));
}
/** Bridge target key: patient upsert */
const BRIDGE_SYNC_TARGET_PATIENT = "patient";
/** Bridge target key: provider ensure (consultant ↔ provider link) */
const BRIDGE_SYNC_TARGET_PROVIDER = "provider";
/** Bridge target key: policy document link (`bridge_policies/{ourSyncId}`) */
const BRIDGE_SYNC_TARGET_POLICY = "policy";
/** Bridge consent binding keys — 1:1 with observed subcollections under `bridge_patients`. */
const BRIDGE_SYNC_TARGET_CONSENT_AOB = "consentAob";
const BRIDGE_SYNC_TARGET_CONSENT_TREAT = "consentTreat";
const BRIDGE_SYNC_TARGET_CONSENT_PRIVACY = "consentPrivacy";
const BRIDGE_CONSENT_SYNC_TARGETS = [
	BRIDGE_SYNC_TARGET_CONSENT_AOB,
	BRIDGE_SYNC_TARGET_CONSENT_TREAT,
	BRIDGE_SYNC_TARGET_CONSENT_PRIVACY
];
/** Bridge Consent API `type` enum values required for clinical booking. */
const bridgeConsentTypeSchema = z$1.enum([
	"ASSIGNMENT_OF_BENEFITS",
	"CONSENT_TO_TREAT",
	"PRIVACY_NOTICE"
]);
const BRIDGE_CONSENT_TYPE_BY_SYNC_TARGET = {
	[BRIDGE_SYNC_TARGET_CONSENT_AOB]: "ASSIGNMENT_OF_BENEFITS",
	[BRIDGE_SYNC_TARGET_CONSENT_TREAT]: "CONSENT_TO_TREAT",
	[BRIDGE_SYNC_TARGET_CONSENT_PRIVACY]: "PRIVACY_NOTICE"
};
/** Observed subcollection segment under `bridge_patients/{customerId}`. */
const BRIDGE_CONSENT_OBSERVED_COLLECTION_BY_SYNC_TARGET = {
	[BRIDGE_SYNC_TARGET_CONSENT_AOB]: BRIDGE_SYNC_TARGET_CONSENT_AOB,
	[BRIDGE_SYNC_TARGET_CONSENT_TREAT]: BRIDGE_SYNC_TARGET_CONSENT_TREAT,
	[BRIDGE_SYNC_TARGET_CONSENT_PRIVACY]: BRIDGE_SYNC_TARGET_CONSENT_PRIVACY
};
/**
* Complete Bridge patient target on customers/vendors/bridge binding.target.
* Bridge I3 reads this only — no customer document reads for demographics.
*/
const bridgePatientVendorTargetSchema = z$1.object({
	customerId: zodFlavorId(),
	firstName: z$1.string().trim().min(1),
	lastName: z$1.string().trim().min(1),
	dateOfBirth: customerDateOfBirthSchema,
	email: z$1.string().trim().min(1),
	mobilePhone: z$1.string().optional()
});
/** @deprecated alias — use bridgePatientVendorTargetSchema */
const bridgePatientTargetDataSchema = bridgePatientVendorTargetSchema;
function parseBridgePatientVendorTarget(raw) {
	const parsed = bridgePatientVendorTargetSchema.safeParse(raw);
	return parsed.success ? parsed.data : void 0;
}
function buildBridgePatientTargetFromCustomer(customer) {
	return bridgePatientVendorTargetSchema.parse({
		customerId: customer.id,
		...pickBridgePatientCustomerSyncFields(customer)
	});
}
/** L3.5 desired Bridge patient payload — sole contract for L4 resolve. */
function buildBridgePatientTarget(customer) {
	return buildBridgePatientTargetFromCustomer(customer);
}
function buildBridgePatientVendorBinding(args) {
	return {
		syncId: args.customerId,
		intent: "ensure",
		desiredRevision: (args.current?.desiredRevision ?? 0) + 1,
		target: args.target
	};
}
const BRIDGE_EVENT_TYPE_PATIENT_RESOLVE = "bridge.patient_resolve";
const bridgePatientResolveEventDataSchema = z$1.object({ customerId: zodFlavorId() });
const BRIDGE_EVENT_TYPE_PROVIDER_RESOLVE = "bridge.provider_resolve";
const bridgeProviderResolveEventDataSchema = z$1.object({
	providerId: zodFlavorId(),
	/** Desired Bridge/L4 `externalId` (consultant cascade link); omit = pull-only. */
	desiredExternalId: z$1.string().optional(),
	/** Consultant cascade unlink — clear Bridge/L4 `externalId` when set. */
	clearExternalId: z$1.literal(true).optional(),
	/**
	* When clearing, only clear if Bridge `externalId` equals this value
	* (consultant cascade passes consultantId).
	*/
	expectedExternalId: z$1.string().optional()
}).superRefine((data, ctx) => {
	if (data.clearExternalId === true && data.desiredExternalId != null) ctx.addIssue({
		code: "custom",
		message: "clearExternalId and desiredExternalId are mutually exclusive",
		path: ["clearExternalId"]
	});
	if (data.expectedExternalId != null && data.clearExternalId !== true) ctx.addIssue({
		code: "custom",
		message: "expectedExternalId requires clearExternalId",
		path: ["expectedExternalId"]
	});
});

export { BRIDGE_CONSENT_OBSERVED_COLLECTION_BY_SYNC_TARGET, BRIDGE_CONSENT_SYNC_TARGETS, BRIDGE_CONSENT_TYPE_BY_SYNC_TARGET, BRIDGE_EVENT_TYPE_PATIENT_RESOLVE, BRIDGE_EVENT_TYPE_PROVIDER_RESOLVE, BRIDGE_PATIENT_CUSTOMER_SYNC_FIELDS, BRIDGE_SYNC_TARGET_CONSENT_AOB, BRIDGE_SYNC_TARGET_CONSENT_PRIVACY, BRIDGE_SYNC_TARGET_CONSENT_TREAT, BRIDGE_SYNC_TARGET_PATIENT, BRIDGE_SYNC_TARGET_POLICY, BRIDGE_SYNC_TARGET_PROVIDER, bridgeConsentTypeSchema, bridgePatientCustomerSyncFieldsChanged, bridgePatientResolveEventDataSchema, bridgePatientTargetDataSchema, bridgePatientVendorTargetSchema, bridgeProviderResolveEventDataSchema, buildBridgePatientTarget, buildBridgePatientTargetFromCustomer, buildBridgePatientVendorBinding, parseBridgePatientVendorTarget, pickBridgePatientCustomerSyncFields };
//# sourceMappingURL=syncTargets.mjs.map